import {
  ArrowRight,
  BadgeCheck,
  Bike,
  ChefHat,
  ChevronDown,
  CircleUserRound,
  Clock3,
  CreditCard,
  Flame,
  Heart,
  LocateFixed,
  LockKeyhole,
  MapPin,
  Menu,
  Minus,
  PackageCheck,
  Phone,
  Plus,
  Search,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Star,
  Store,
  Trash2,
  UserRound,
  WalletCards,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, post } from "./api";
import Modal from "./components/Modal";
import Toast, { type ToastData } from "./components/Toast";
import { dateTime, money, paymentLabel, phoneMask, statusLabel } from "./lib";
import type {
  CartItem,
  CartSelection,
  Customer,
  OrderSummary,
  Product,
  StoreConfig,
} from "./types";

type AuthMode = "login" | "register";
type CheckoutStep = "fulfillment" | "address" | "payment" | "review";

const emptyRegister = {
  name: "",
  phone: "",
  email: "",
  password: "",
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  zipcode: "",
  reference: "",
  latitude: null as number | null,
  longitude: null as number | null,
};

const cartTotal = (items: CartItem[]) =>
  items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);

export function ProductDialog({
  product,
  onClose,
  onAdd,
}: {
  product: Product | null;
  onClose: () => void;
  onAdd: (item: CartItem) => void;
}) {
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [validation, setValidation] = useState("");

  useEffect(() => {
    setSelected({});
    setQuantity(1);
    setNotes("");
    setValidation("");
  }, [product]);

  if (!product) return null;

  const selections: CartSelection[] = product.optionGroups.flatMap((group) =>
    (selected[group.id] ?? []).flatMap((optionId) => {
      const option = group.options.find((candidate) => candidate.id === optionId);
      return option
        ? [
            {
              groupId: group.id,
              groupName: group.name,
              optionId: option.id,
              optionName: option.name,
              price: option.price,
            },
          ]
        : [];
    }),
  );
  const unitPrice =
    product.basePrice + selections.reduce((sum, option) => sum + option.price, 0);
  const selectionsValid = product.optionGroups.every((group) => {
    const count = selected[group.id]?.length ?? 0;
    return count >= group.min && count <= group.max;
  });

  const toggle = (groupId: string, optionId: string, max: number) => {
    setValidation("");
    setSelected((current) => {
      const values = current[groupId] ?? [];
      if (values.includes(optionId))
        return { ...current, [groupId]: values.filter((id) => id !== optionId) };
      if (max === 1) return { ...current, [groupId]: [optionId] };
      if (values.length >= max) return current;
      return { ...current, [groupId]: [...values, optionId] };
    });
  };

  const submit = () => {
    for (const group of product.optionGroups) {
      const count = selected[group.id]?.length ?? 0;
      if (count < group.min || count > group.max) {
        setValidation(
          `${group.name}: escolha ${group.min === group.max ? group.min : `de ${group.min} a ${group.max}`} opção(ões).`,
        );
        return;
      }
    }
    onAdd({
      cartId: crypto.randomUUID(),
      productId: product.id,
      name: product.name,
      image: product.image,
      quantity,
      unitPrice,
      selections,
      notes,
    });
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={product.name}
      eyebrow={product.isPromotion ? "Oferta Max House" : "Monte do seu jeito"}
      wide
    >
      <div className="product-dialog-grid">
        <aside className="product-dialog-cover">
          <img src={product.image || "/assets/maxhouse-background.png"} alt="" />
          <div className="product-dialog-cover-copy">
            {product.description && <p>{product.description}</p>}
            <div className="price-block">
              {product.strikePrice ? <del>{money(product.strikePrice)}</del> : null}
              <strong>
                {unitPrice > 0 ? money(unitPrice) : money(product.fromPrice || 0)}
              </strong>
            </div>
          </div>
        </aside>
        <div className="option-flow">
          {product.optionGroups.map((group, groupIndex) => (
            <section className="option-section" key={group.id}>
              <div className="option-title-row">
                <span className="option-step">{String(groupIndex + 1).padStart(2, "0")}</span>
                <div>
                  <h3>{group.name}</h3>
                  <p>
                    {group.min === group.max
                      ? `Escolha ${group.min}`
                      : `Escolha de ${group.min} a ${group.max}`}
                  </p>
                </div>
                <span className="required-chip">{group.min > 0 ? "Obrigatório" : "Opcional"}</span>
              </div>
              <div className="option-list">
                {group.options.map((option) => {
                  const checked = selected[group.id]?.includes(option.id) ?? false;
                  const locked =
                    !checked &&
                    (selected[group.id]?.length ?? 0) >= group.max &&
                    group.max > 1;
                  return (
                    <label
                      className={`option-card ${checked ? "option-selected" : ""} ${locked ? "option-locked" : ""}`}
                      key={option.id}
                    >
                      <input
                        type={group.max === 1 ? "radio" : "checkbox"}
                        name={group.id}
                        checked={checked}
                        disabled={locked}
                        onChange={() => toggle(group.id, option.id, group.max)}
                      />
                      <span className="fake-control" />
                      <span className="option-copy">
                        <strong>{option.name}</strong>
                        {option.description && <small>{option.description}</small>}
                      </span>
                      <span className="option-price">
                        {option.tier && <em className={`tier tier-${option.tier}`}>{option.tier}</em>}
                        {option.price > 0 ? money(option.price) : "Incluso"}
                      </span>
                    </label>
                  );
                })}
              </div>
            </section>
          ))}
          <section className="option-section">
            <div className="option-title-row">
              <span className="option-step">+</span>
              <div>
                <h3>Algum detalhe?</h3>
                <p>A cozinha recebe esta observação junto do item.</p>
              </div>
            </div>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Ex.: sem cebola, cortar em 8 pedaços..."
              maxLength={400}
            />
          </section>
          {validation && <div className="inline-error">{validation}</div>}
          <footer className="product-dialog-footer">
            <div className="quantity-stepper" aria-label="Quantidade">
              <button onClick={() => setQuantity((value) => Math.max(1, value - 1))}>
                <Minus size={18} />
              </button>
              <strong>{quantity}</strong>
              <button onClick={() => setQuantity((value) => Math.min(20, value + 1))}>
                <Plus size={18} />
              </button>
            </div>
            <button className="primary-button grow" disabled={!selectionsValid} onClick={submit}>
              {selectionsValid ? "Adicionar" : "Selecione as opções"}
              <span>{selectionsValid ? money(unitPrice * quantity) : `a partir de ${money(product.fromPrice || 0)}`}</span>
            </button>
          </footer>
        </div>
      </div>
    </Modal>
  );
}

function AuthDialog({
  open,
  onClose,
  onAuthenticated,
  initialMode = "login",
}: {
  open: boolean;
  onClose: () => void;
  onAuthenticated: (customer: Customer) => void;
  initialMode?: AuthMode;
}) {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [login, setLogin] = useState({ login: "", password: "" });
  const [register, setRegister] = useState(emptyRegister);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [locationBusy, setLocationBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setError("");
    }
  }, [open, initialMode]);

  const submitLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await post<{ customer: Customer }>("auth/login", login);
      onAuthenticated(response.customer);
      onClose();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Falha no login.");
    } finally {
      setBusy(false);
    }
  };

  const submitRegister = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await post<{ customer: Customer }>("auth/register", {
        name: register.name,
        phone: register.phone,
        email: register.email,
        password: register.password,
        marketingOptIn: true,
        address: {
          label: "Casa",
          street: register.street,
          number: register.number,
          complement: register.complement,
          neighborhood: register.neighborhood,
          city: "Matinhos",
          state: "PR",
          zipcode: register.zipcode,
          reference: register.reference,
          latitude: register.latitude,
          longitude: register.longitude,
        },
      });
      onAuthenticated(response.customer);
      onClose();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Falha no cadastro.");
    } finally {
      setBusy(false);
    }
  };

  const captureLocation = () => {
    if (!navigator.geolocation) {
      setError("Este dispositivo não oferece localização.");
      return;
    }
    setLocationBusy(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setRegister((current) => ({
          ...current,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }));
        setLocationBusy(false);
      },
      () => {
        setError("Não foi possível obter a localização. Preencha o endereço normalmente.");
        setLocationBusy(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  return (
    <Modal open={open} onClose={onClose} title="Sua conta Max House" eyebrow="Pedido seguro">
      <div className="auth-intro">
        <span className="auth-icon">
          <LockKeyhole size={22} />
        </span>
        <div>
          <strong>Seu endereço vai certo até o motoboy.</strong>
          <p>Cadastre uma vez e acompanhe todos os pedidos em tempo real.</p>
        </div>
      </div>
      <div className="segmented">
        <button className={mode === "login" ? "active" : ""} onClick={() => setMode("login")}>
          Já tenho conta
        </button>
        <button className={mode === "register" ? "active" : ""} onClick={() => setMode("register")}>
          Quero me cadastrar
        </button>
      </div>
      {mode === "login" ? (
        <form className="stack-form" onSubmit={submitLogin}>
          <label>
            Telefone ou e-mail
            <input
              value={login.login}
              onChange={(event) => setLogin({ ...login, login: event.target.value })}
              placeholder="(41) 99999-9999"
              autoComplete="username"
              required
            />
          </label>
          <label>
            Senha
            <input
              type="password"
              value={login.password}
              onChange={(event) => setLogin({ ...login, password: event.target.value })}
              placeholder="Sua senha"
              autoComplete="current-password"
              required
            />
          </label>
          {error && <div className="inline-error">{error}</div>}
          <button className="primary-button" disabled={busy}>
            {busy ? "Entrando..." : "Entrar e continuar"}
            {!busy && <ArrowRight size={18} />}
          </button>
        </form>
      ) : (
        <form className="stack-form" onSubmit={submitRegister}>
          <div className="form-section-title">
            <UserRound size={18} />
            <span>Seus dados</span>
          </div>
          <div className="form-grid">
            <label className="span-2">
              Nome completo
              <input
                value={register.name}
                onChange={(event) => setRegister({ ...register, name: event.target.value })}
                autoComplete="name"
                required
              />
            </label>
            <label>
              WhatsApp
              <input
                value={phoneMask(register.phone)}
                onChange={(event) => setRegister({ ...register, phone: event.target.value })}
                inputMode="tel"
                autoComplete="tel"
                required
              />
            </label>
            <label>
              E-mail
              <input
                type="email"
                value={register.email}
                onChange={(event) => setRegister({ ...register, email: event.target.value })}
                autoComplete="email"
              />
            </label>
            <label className="span-2">
              Crie uma senha
              <input
                type="password"
                minLength={8}
                value={register.password}
                onChange={(event) => setRegister({ ...register, password: event.target.value })}
                autoComplete="new-password"
                required
              />
            </label>
          </div>
          <div className="form-section-title">
            <MapPin size={18} />
            <span>Endereço de entrega</span>
          </div>
          <div className="form-grid">
            <label className="span-2">
              Rua / Avenida
              <input
                value={register.street}
                onChange={(event) => setRegister({ ...register, street: event.target.value })}
                autoComplete="street-address"
                required
              />
            </label>
            <label>
              Número
              <input
                value={register.number}
                onChange={(event) => setRegister({ ...register, number: event.target.value })}
                required
              />
            </label>
            <label>
              Bairro
              <input
                value={register.neighborhood}
                onChange={(event) => setRegister({ ...register, neighborhood: event.target.value })}
                required
              />
            </label>
            <label>
              Complemento
              <input
                value={register.complement}
                onChange={(event) => setRegister({ ...register, complement: event.target.value })}
              />
            </label>
            <label>
              CEP
              <input
                value={register.zipcode}
                onChange={(event) => setRegister({ ...register, zipcode: event.target.value })}
                inputMode="numeric"
              />
            </label>
            <label className="span-2">
              Ponto de referência
              <input
                value={register.reference}
                onChange={(event) => setRegister({ ...register, reference: event.target.value })}
              />
            </label>
          </div>
          <button type="button" className="location-button" onClick={captureLocation}>
            <LocateFixed size={18} />
            {register.latitude
              ? "Localização precisa adicionada"
              : locationBusy
                ? "Obtendo localização..."
                : "Usar minha localização para ajudar o motoboy"}
            {register.latitude && <BadgeCheck size={18} />}
          </button>
          {error && <div className="inline-error">{error}</div>}
          <button className="primary-button" disabled={busy}>
            {busy ? "Criando sua conta..." : "Criar conta e pedir"}
            {!busy && <ArrowRight size={18} />}
          </button>
          <p className="form-disclaimer">
            Ao continuar, você autoriza o uso dos dados somente para atendimento, entrega e
            histórico dos seus pedidos.
          </p>
        </form>
      )}
    </Modal>
  );
}

export default function Storefront() {
  const [store, setStore] = useState<StoreConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [fatalError, setFatalError] = useState("");
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("maxhouse-cart") || "[]") as CartItem[];
    } catch {
      return [];
    }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>("login");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState<CheckoutStep>("fulfillment");
  const [fulfillment, setFulfillment] = useState<"delivery" | "pickup">("delivery");
  const [addressId, setAddressId] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("pix");
  const [changeFor, setChangeFor] = useState("");
  const [orderNotes, setOrderNotes] = useState("");
  const [placingOrder, setPlacingOrder] = useState(false);
  const [placedOrder, setPlacedOrder] = useState<OrderSummary | null>(null);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [toast, setToast] = useState<ToastData | null>(null);
  const toastCounter = useRef(0);

  const showToast = (message: string, tone: "success" | "error" = "success") => {
    toastCounter.current += 1;
    setToast({ id: toastCounter.current, message, tone });
  };

  useEffect(() => {
    Promise.allSettled([
      api<{ store: StoreConfig }>("public/config"),
      api<{ customer: Customer }>("auth/me"),
    ]).then(([configResult, customerResult]) => {
      if (configResult.status === "fulfilled") setStore(configResult.value.store);
      else setFatalError("O cardápio está sendo atualizado. Tente novamente em instantes.");
      if (customerResult.status === "fulfilled") {
        setCustomer(customerResult.value.customer);
        setAddressId(customerResult.value.customer.addresses[0]?.id || "");
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    localStorage.setItem("maxhouse-cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), 3600);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  const products = useMemo(() => {
    if (!store) return [];
    const normalized = search.trim().toLocaleLowerCase("pt-BR");
    return store.categories.flatMap((category) =>
      category.products.filter((product) => {
        if (selectedCategory !== "all" && product.categoryId !== selectedCategory) return false;
        if (!normalized) return true;
        return `${product.name} ${product.description || ""}`
          .toLocaleLowerCase("pt-BR")
          .includes(normalized);
      }),
    );
  }, [store, search, selectedCategory]);

  const addToCart = (item: CartItem) => {
    setCart((current) => [...current, item]);
    showToast(`${item.name} entrou no carrinho.`);
  };

  const updateQuantity = (cartId: string, delta: number) => {
    setCart((current) =>
      current.flatMap((item) => {
        if (item.cartId !== cartId) return [item];
        const quantity = item.quantity + delta;
        return quantity <= 0 ? [] : [{ ...item, quantity: Math.min(20, quantity) }];
      }),
    );
  };

  const startCheckout = () => {
    if (!cart.length) return;
    setCartOpen(false);
    if (!customer) {
      setAuthMode("register");
      setAuthOpen(true);
      return;
    }
    setAddressId((current) => current || customer.addresses[0]?.id || "");
    setCheckoutStep("fulfillment");
    setCheckoutOpen(true);
  };

  const handleAuthenticated = (nextCustomer: Customer) => {
    setCustomer(nextCustomer);
    setAddressId(nextCustomer.addresses[0]?.id || "");
    if (cart.length) {
      window.setTimeout(() => {
        setCheckoutStep("fulfillment");
        setCheckoutOpen(true);
      }, 160);
    }
    showToast(`Bem-vindo, ${nextCustomer.name.split(" ")[0]}!`);
  };

  const zone = store?.deliveryZones.find((candidate) => candidate.id === zoneId);
  const subtotal = cartTotal(cart);
  const deliveryFee = fulfillment === "delivery" ? zone?.fee || 0 : 0;
  const total = subtotal + deliveryFee;

  const placeOrder = async () => {
    if (!customer || !store) return;
    setPlacingOrder(true);
    try {
      const response = await post<{ order: OrderSummary }>("orders", {
        source: "store",
        items: cart.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          selections: item.selections.map((selection) => selection.optionId),
          notes: item.notes,
        })),
        fulfillmentType: fulfillment,
        addressId: fulfillment === "delivery" ? addressId : null,
        deliveryZoneId: fulfillment === "delivery" ? zoneId : null,
        paymentMethod,
        changeFor: paymentMethod === "cash" && changeFor ? Number(changeFor.replace(",", ".")) : null,
        notes: orderNotes,
      });
      setPlacedOrder(response.order);
      setCart([]);
      setCheckoutOpen(false);
      showToast(`Pedido #${response.order.number} enviado!`);
    } catch (requestError) {
      showToast(
        requestError instanceof Error ? requestError.message : "Não foi possível enviar o pedido.",
        "error",
      );
    } finally {
      setPlacingOrder(false);
    }
  };

  const loadOrders = async () => {
    if (!customer) return;
    try {
      const response = await api<{ orders: OrderSummary[] }>("orders");
      setOrders(response.orders);
      setOrdersOpen(true);
    } catch (requestError) {
      showToast(requestError instanceof Error ? requestError.message : "Falha ao buscar pedidos.", "error");
    }
  };

  const logout = async () => {
    await post("auth/logout");
    setCustomer(null);
    setOrdersOpen(false);
    showToast("Você saiu da sua conta.");
  };

  if (loading)
    return (
      <div className="splash-screen">
        <img src="/assets/maxhouse-logo.png" alt="Max House" />
        <div className="loading-line" />
        <p>Aquecendo o forno...</p>
      </div>
    );
  if (!store || fatalError)
    return (
      <div className="empty-screen">
        <ChefHat size={44} />
        <h1>Voltamos em instantes</h1>
        <p>{fatalError}</p>
      </div>
    );

  return (
    <div className="storefront">
      <header className="store-header">
        <a className="brand" href="/" aria-label="Max House Pizzas">
          <img src={store.logo} alt="" />
          <span>
            <strong>MAX HOUSE</strong>
            <small>PIZZAS DELIVERY</small>
          </span>
        </a>
        <nav className="desktop-nav">
          <a href="#cardapio">Cardápio</a>
          <a href="#como-funciona">Como funciona</a>
          <a href={`https://wa.me/55${store.whatsapp}`} target="_blank" rel="noreferrer">
            Atendimento
          </a>
        </nav>
        <div className="header-actions">
          <button
            className="account-button"
            onClick={() => {
              if (customer) loadOrders();
              else {
                setAuthMode("login");
                setAuthOpen(true);
              }
            }}
          >
            <CircleUserRound size={20} />
            <span>{customer ? customer.name.split(" ")[0] : "Entrar"}</span>
          </button>
          <button className="cart-button" onClick={() => setCartOpen(true)}>
            <ShoppingBag size={20} />
            <span>Carrinho</span>
            <b>{cart.reduce((sum, item) => sum + item.quantity, 0)}</b>
          </button>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="hero-image" style={{ backgroundImage: `url(${store.background})` }} />
          <div className="hero-noise" />
          <div className="hero-content">
            <div className="hero-kicker">
              <span className="pulse-dot" />
              <span>Aberto hoje · 18h às 23h</span>
            </div>
            <h1>
              Sua noite pede
              <em>uma Max.</em>
            </h1>
            <p>
              Massa artesanal, recheio generoso e sabores que chegam quentinhos até você.
              Escolha cada detalhe da sua pizza.
            </p>
            <div className="hero-actions">
              <a href="#cardapio" className="primary-button hero-cta">
                Ver cardápio
                <ArrowRight size={19} />
              </a>
              <div className="rating-proof">
                <span className="rating-stars">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <Star key={value} size={15} fill="currentColor" />
                  ))}
                </span>
                <span>
                  <strong>{store.rating.toFixed(1)}</strong> avaliação dos clientes
                </span>
              </div>
            </div>
          </div>
          <div className="hero-card hero-card-time">
            <Clock3 size={22} />
            <span>
              <small>Entrega estimada</small>
              <strong>{store.deliveryWait} min</strong>
            </span>
          </div>
          <div className="hero-card hero-card-pickup">
            <Store size={22} />
            <span>
              <small>Retire quentinha</small>
              <strong>{store.pickupWait} min</strong>
            </span>
          </div>
        </section>

        <section className="trust-strip">
          <div>
            <span className="trust-icon"><Flame size={20} /></span>
            <span><strong>Sai do forno</strong><small>direto para sua mesa</small></span>
          </div>
          <div>
            <span className="trust-icon"><Bike size={20} /></span>
            <span><strong>Entrega rastreável</strong><small>Maps e Waze no pedido</small></span>
          </div>
          <div>
            <span className="trust-icon"><ShieldCheck size={20} /></span>
            <span><strong>Pedido protegido</strong><small>cadastro e histórico seguro</small></span>
          </div>
          <div>
            <span className="trust-icon"><WalletCards size={20} /></span>
            <span><strong>Pagamento fácil</strong><small>Pix, dinheiro ou cartão</small></span>
          </div>
        </section>

        <section className="menu-section" id="cardapio">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Cardápio Max House</span>
              <h2>Qual vai ser a de hoje?</h2>
            </div>
            <div className="search-box">
              <Search size={19} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Busque um sabor ou produto"
              />
              {search && (
                <button onClick={() => setSearch("")} aria-label="Limpar busca">
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
          <div className="category-tabs">
            <button
              className={selectedCategory === "all" ? "active" : ""}
              onClick={() => setSelectedCategory("all")}
            >
              Todos
            </button>
            {store.categories.map((category) => (
              <button
                key={category.id}
                className={selectedCategory === category.id ? "active" : ""}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.name}
              </button>
            ))}
          </div>
          {products.length ? (
            <div className="product-grid">
              {products.map((product) => {
                const shownPrice = product.basePrice || product.fromPrice || 0;
                return (
                  <article className="product-card" key={product.id}>
                    <button className="product-click-area" onClick={() => setSelectedProduct(product)}>
                      <div className="product-image-wrap">
                        <img
                          src={product.image || "/assets/maxhouse-background.png"}
                          alt={product.name}
                          loading="lazy"
                        />
                        {product.isPromotion && <span className="promo-badge"><Sparkles size={14} /> Oferta</span>}
                        {product.isAlcohol && <span className="age-badge">+18</span>}
                        <span className="favorite-chip"><Heart size={16} /></span>
                      </div>
                      <div className="product-card-copy">
                        <div>
                          <h3>{product.name}</h3>
                          <p>{product.description || "Prepare do seu jeito e escolha cada detalhe."}</p>
                        </div>
                        <footer>
                          <span className="product-price">
                            {product.fromPrice ? <small>A partir de</small> : null}
                            {product.strikePrice ? <del>{money(product.strikePrice)}</del> : null}
                            <strong>{money(shownPrice)}</strong>
                          </span>
                          <span className="add-chip"><Plus size={19} /></span>
                        </footer>
                      </div>
                    </button>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="no-results">
              <Search size={34} />
              <h3>Nenhum sabor encontrado</h3>
              <p>Tente buscar por outro ingrediente.</p>
            </div>
          )}
        </section>

        <section className="how-section" id="como-funciona">
          <div className="section-heading centered">
            <div>
              <span className="eyebrow">Do clique à campainha</span>
              <h2>Seu pedido sem complicação</h2>
            </div>
          </div>
          <div className="how-grid">
            {[
              { icon: Menu, number: "01", title: "Monte sua Max", text: "Escolha tamanho, sabores e borda. O valor é calculado na hora." },
              { icon: LockKeyhole, number: "02", title: "Entre ou cadastre-se", text: "Seu endereço fica salvo e gera as rotas de Maps e Waze." },
              { icon: ChefHat, number: "03", title: "Acompanhe a cozinha", text: "Pix confirmado entra na cozinha; outros pagamentos passam direto." },
              { icon: Bike, number: "04", title: "Receba quentinha", text: "O motoboy leva telefone, endereço e navegação já preparados." },
            ].map(({ icon: Icon, number, title, text }) => (
              <article key={number}>
                <span className="how-number">{number}</span>
                <span className="how-icon"><Icon size={24} /></span>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="location-banner">
          <div>
            <span className="eyebrow">Nossa casa em Matinhos</span>
            <h2>Retire sua pizza direto do forno</h2>
            <p>{store.address} · {store.reference}</p>
          </div>
          <a
            className="secondary-button"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${store.address}, ${store.city} - ${store.state}`)}`}
            target="_blank"
            rel="noreferrer"
          >
            <MapPin size={18} />
            Abrir no Maps
          </a>
        </section>
      </main>

      <footer className="store-footer">
        <div className="footer-brand">
          <img src={store.logo} alt="" />
          <div>
            <strong>MAX HOUSE</strong>
            <small>A pizza que transforma sua noite.</small>
          </div>
        </div>
        <div>
          <strong>Atendimento</strong>
          <a href={`tel:+55${store.phone}`}><Phone size={15} /> {phoneMask(store.phone.slice(-11))}</a>
          <span><Clock3 size={15} /> {store.hours}</span>
        </div>
        <div>
          <strong>Operação</strong>
          <a href="/gestao">Portal da equipe</a>
          <span>Max House Pizzas Delivery</span>
        </div>
        <p>© 2026 Max House. Todos os direitos reservados.</p>
      </footer>

      <button className="mobile-cart-bar" onClick={() => setCartOpen(true)}>
        <span><ShoppingBag size={19} /> {cart.reduce((sum, item) => sum + item.quantity, 0)} item(ns)</span>
        <strong>{money(subtotal)}</strong>
      </button>

      <ProductDialog product={selectedProduct} onClose={() => setSelectedProduct(null)} onAdd={addToCart} />

      <Modal open={cartOpen} onClose={() => setCartOpen(false)} title="Seu carrinho" eyebrow="Quase lá" drawer>
        {cart.length ? (
          <>
            <div className="cart-list">
              {cart.map((item) => (
                <article className="cart-item" key={item.cartId}>
                  <img src={item.image || "/assets/maxhouse-background.png"} alt="" />
                  <div className="cart-item-copy">
                    <h3>{item.name}</h3>
                    {item.selections.map((selection) => (
                      <small key={`${item.cartId}-${selection.optionId}`}>{selection.optionName}</small>
                    ))}
                    {item.notes && <small className="cart-note">“{item.notes}”</small>}
                    <strong>{money(item.unitPrice * item.quantity)}</strong>
                  </div>
                  <div className="cart-quantity">
                    <button onClick={() => updateQuantity(item.cartId, -1)}>
                      {item.quantity === 1 ? <Trash2 size={15} /> : <Minus size={15} />}
                    </button>
                    <span>{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.cartId, 1)}><Plus size={15} /></button>
                  </div>
                </article>
              ))}
            </div>
            <div className="cart-summary">
              <span>Subtotal <strong>{money(subtotal)}</strong></span>
              <p>A taxa de entrega será calculada pelo bairro.</p>
              <button className="primary-button" onClick={startCheckout}>
                Continuar pedido <ArrowRight size={18} />
              </button>
            </div>
          </>
        ) : (
          <div className="empty-cart">
            <ShoppingBag size={42} />
            <h3>Seu carrinho está vazio</h3>
            <p>Escolha a pizza que vai transformar sua noite.</p>
            <button className="primary-button" onClick={() => setCartOpen(false)}>Ver cardápio</button>
          </div>
        )}
      </Modal>

      <AuthDialog
        open={authOpen}
        initialMode={authMode}
        onClose={() => setAuthOpen(false)}
        onAuthenticated={handleAuthenticated}
      />

      <Modal open={checkoutOpen} onClose={() => setCheckoutOpen(false)} title="Finalizar pedido" eyebrow="Checkout seguro" wide>
        <div className="checkout-layout">
          <div className="checkout-main">
            <div className="checkout-progress">
              {(["fulfillment", "address", "payment", "review"] as CheckoutStep[]).map((step, index) => (
                <button
                  key={step}
                  className={checkoutStep === step ? "active" : ""}
                  onClick={() => setCheckoutStep(step)}
                >
                  <span>{index + 1}</span>
                  {["Receber", "Endereço", "Pagamento", "Revisão"][index]}
                </button>
              ))}
            </div>
            {checkoutStep === "fulfillment" && (
              <section className="checkout-panel">
                <span className="eyebrow">Como você quer receber?</span>
                <div className="choice-cards">
                  <button className={fulfillment === "delivery" ? "selected" : ""} onClick={() => setFulfillment("delivery")}>
                    <Bike size={25} />
                    <span><strong>Entrega</strong><small>Receba em cerca de {store.deliveryWait} min</small></span>
                    <span className="choice-radio" />
                  </button>
                  <button className={fulfillment === "pickup" ? "selected" : ""} onClick={() => setFulfillment("pickup")}>
                    <Store size={25} />
                    <span><strong>Retirada</strong><small>Pronto em cerca de {store.pickupWait} min</small></span>
                    <span className="choice-radio" />
                  </button>
                </div>
                <button className="primary-button" onClick={() => setCheckoutStep(fulfillment === "delivery" ? "address" : "payment")}>
                  Continuar <ArrowRight size={18} />
                </button>
              </section>
            )}
            {checkoutStep === "address" && (
              <section className="checkout-panel">
                <span className="eyebrow">Endereço e taxa</span>
                <h3>Onde vamos entregar?</h3>
                <div className="address-options">
                  {customer?.addresses.map((address) => (
                    <label className={addressId === address.id ? "selected" : ""} key={address.id}>
                      <input
                        type="radio"
                        checked={addressId === address.id}
                        onChange={() => setAddressId(address.id || "")}
                      />
                      <MapPin size={20} />
                      <span>
                        <strong>{address.label}</strong>
                        <small>{address.street}, {address.number} · {address.neighborhood}</small>
                      </span>
                      <BadgeCheck size={18} />
                    </label>
                  ))}
                </div>
                <label className="select-label">
                  Selecione seu bairro para calcular a entrega
                  <span className="select-wrap">
                    <select value={zoneId} onChange={(event) => setZoneId(event.target.value)} required>
                      <option value="">Escolha o bairro</option>
                      {store.deliveryZones.map((deliveryZone) => (
                        <option value={deliveryZone.id} key={deliveryZone.id}>
                          {deliveryZone.name} · {money(deliveryZone.fee)}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={18} />
                  </span>
                </label>
                <div className="checkout-buttons">
                  <button className="ghost-button" onClick={() => setCheckoutStep("fulfillment")}>Voltar</button>
                  <button className="primary-button" disabled={!addressId || !zoneId} onClick={() => setCheckoutStep("payment")}>
                    Continuar <ArrowRight size={18} />
                  </button>
                </div>
              </section>
            )}
            {checkoutStep === "payment" && (
              <section className="checkout-panel">
                <span className="eyebrow">Pagamento</span>
                <h3>Como prefere pagar?</h3>
                <div className="payment-options">
                  {[
                    { id: "pix", icon: Sparkles, title: "Pix", text: "A cozinha recebe após confirmação" },
                    { id: "credit", icon: CreditCard, title: "Crédito", text: "Maquininha na entrega" },
                    { id: "debit", icon: WalletCards, title: "Débito", text: "Maquininha na entrega" },
                    { id: "cash", icon: WalletCards, title: "Dinheiro", text: "Informe o troco se precisar" },
                  ].map(({ id, icon: Icon, title, text }) => (
                    <label className={paymentMethod === id ? "selected" : ""} key={id}>
                      <input
                        type="radio"
                        name="checkout-payment"
                        checked={paymentMethod === id}
                        onChange={() => setPaymentMethod(id)}
                      />
                      <Icon size={21} />
                      <span><strong>{title}</strong><small>{text}</small></span>
                      <span className="choice-radio" />
                    </label>
                  ))}
                </div>
                {paymentMethod === "pix" && (
                  <div className="pix-explainer">
                    <ShieldCheck size={20} />
                    <p>Seu pedido ficará em <strong>Aguardando Pix</strong>. Assim que a equipe confirmar, ele entra automaticamente na cozinha.</p>
                  </div>
                )}
                {paymentMethod === "cash" && (
                  <label>
                    Troco para quanto?
                    <input value={changeFor} onChange={(event) => setChangeFor(event.target.value)} inputMode="decimal" placeholder="Ex.: 100,00" />
                  </label>
                )}
                <div className="checkout-buttons">
                  <button className="ghost-button" onClick={() => setCheckoutStep(fulfillment === "delivery" ? "address" : "fulfillment")}>Voltar</button>
                  <button className="primary-button" onClick={() => setCheckoutStep("review")}>Revisar <ArrowRight size={18} /></button>
                </div>
              </section>
            )}
            {checkoutStep === "review" && (
              <section className="checkout-panel">
                <span className="eyebrow">Confira seu pedido</span>
                <h3>Tudo certo para enviar?</h3>
                <div className="review-list">
                  {cart.map((item) => (
                    <div key={item.cartId}>
                      <span><b>{item.quantity}×</b> {item.name}</span>
                      <strong>{money(item.unitPrice * item.quantity)}</strong>
                    </div>
                  ))}
                </div>
                <label>
                  Observação geral
                  <textarea value={orderNotes} onChange={(event) => setOrderNotes(event.target.value)} placeholder="Ex.: chamar no portão..." maxLength={600} />
                </label>
                <div className="review-meta">
                  <span>{fulfillment === "delivery" ? <Bike size={18} /> : <Store size={18} />}{fulfillment === "delivery" ? "Entrega" : "Retirada"}</span>
                  <span><CreditCard size={18} />{paymentLabel[paymentMethod]}</span>
                </div>
                <div className="checkout-buttons">
                  <button className="ghost-button" onClick={() => setCheckoutStep("payment")}>Voltar</button>
                  <button className="primary-button" disabled={placingOrder} onClick={placeOrder}>
                    {placingOrder ? "Enviando..." : `Enviar pedido · ${money(total)}`}
                  </button>
                </div>
              </section>
            )}
          </div>
          <aside className="checkout-summary">
            <div className="checkout-customer">
              <CircleUserRound size={23} />
              <span><small>Pedido para</small><strong>{customer?.name}</strong></span>
              <BadgeCheck size={18} />
            </div>
            <h3>Resumo</h3>
            {cart.map((item) => (
              <div className="summary-line" key={item.cartId}>
                <span>{item.quantity}× {item.name}</span>
                <strong>{money(item.unitPrice * item.quantity)}</strong>
              </div>
            ))}
            <hr />
            <div className="summary-line"><span>Subtotal</span><strong>{money(subtotal)}</strong></div>
            <div className="summary-line"><span>Entrega</span><strong>{fulfillment === "pickup" ? "Grátis" : zone ? money(deliveryFee) : "A calcular"}</strong></div>
            <div className="summary-total"><span>Total</span><strong>{money(total)}</strong></div>
            <div className="secure-note"><LockKeyhole size={16} /> Pedido protegido e salvo na sua conta</div>
          </aside>
        </div>
      </Modal>

      <Modal open={Boolean(placedOrder)} onClose={() => setPlacedOrder(null)} title={`Pedido #${placedOrder?.number}`} eyebrow="Recebemos seu pedido">
        {placedOrder && (
          <div className="order-success">
            <span className="success-orbit"><PackageCheck size={38} /></span>
            <h3>{placedOrder.paymentMethod === "pix" ? "Agora falta confirmar o Pix" : "Já mandamos para a cozinha!"}</h3>
            <p>
              {placedOrder.paymentMethod === "pix"
                ? "Faça o Pix usando a chave abaixo. A equipe confirma o pagamento e libera seu pedido para preparo."
                : "Você pode acompanhar cada etapa entrando em Meus pedidos."}
            </p>
            {placedOrder.paymentMethod === "pix" && (
              <div className="pix-card">
                <span><small>Chave Pix · CNPJ</small><strong>{store.pixKey}</strong></span>
                <button onClick={() => navigator.clipboard.writeText(store.pixKey)}>Copiar chave</button>
              </div>
            )}
            <div className={`status-pill status-${placedOrder.status}`}>{statusLabel[placedOrder.status]}</div>
            <button className="primary-button" onClick={() => { setPlacedOrder(null); loadOrders(); }}>
              Acompanhar pedido
            </button>
          </div>
        )}
      </Modal>

      <Modal open={ordersOpen} onClose={() => setOrdersOpen(false)} title="Meus pedidos" eyebrow={customer?.name}>
        <div className="account-toolbar">
          <span><UserRound size={18} /> {customer?.phone}</span>
          <button onClick={logout}>Sair da conta</button>
        </div>
        <div className="order-history">
          {orders.length ? orders.map((order) => (
            <article key={order.id}>
              <header>
                <span><small>Pedido</small><strong>#{order.number}</strong></span>
                <span className={`status-pill status-${order.status}`}>{statusLabel[order.status]}</span>
              </header>
              <p>{order.items.map((item) => `${item.quantity}× ${item.name}`).join(" · ")}</p>
              <footer>
                <span>{dateTime(order.createdAt)}</span>
                <strong>{money(order.total)}</strong>
              </footer>
            </article>
          )) : (
            <div className="empty-cart"><PackageCheck size={38} /><h3>Nenhum pedido ainda</h3><p>Sua próxima pizza começa no cardápio.</p></div>
          )}
        </div>
      </Modal>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
